import { Fragment, createContext, useContext, type ReactNode } from 'react';
import type { ActivityDto, ItemDto, ShelfDto } from '../../shared/api';
import { POS, type Pos } from '../../shared/domain';
import type { Data } from '../api';

export interface StartReq {
  action: 'register' | 'take' | 'move';
  no?: string;
  /** For "Fixed, send back": go straight to the target shelf. */
  toShelf?: string;
  comment?: string;
}

export interface AppCtx {
  d: Data;
  toast(msg: string): void;
  openItem(no: string): void;
  openArticle(id: number): void;
  closeDialog(): void;
  go(path: string): void;
  startAction(r: StartReq): void;
}
export const Ctx = createContext<AppCtx | null>(null);
export const useApp = () => useContext(Ctx)!;

export const ymd = (iso: string) => {
  const x = new Date(iso);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
export const hm = (iso: string) => {
  const x = new Date(iso);
  return `${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`;
};
export const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
export const pl = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

/** Render text with **bold** markers. */
export function Rich({ t }: { t: string }) {
  return (
    <>
      {t.split('**').map((p, k) => (k % 2 ? <b key={k}>{p}</b> : <Fragment key={k}>{p}</Fragment>))}
    </>
  );
}

export function Tag({ cls = '', dot, children }: { cls?: string; dot?: boolean; children: ReactNode }) {
  return <span className={`tag ${cls} ${dot ? 'dot' : ''}`}>{children}</span>;
}

export function QcTag({ i }: { i: ItemDto }) {
  const { d } = useApp();
  const q = d.qcOf(i);
  return (
    <Tag cls={q.cls} dot>
      {q.t}
    </Tag>
  );
}

export function ItemLink({ no }: { no: string }) {
  const { openItem } = useApp();
  if (!no) return null;
  return (
    <button className="linkbtn code" onClick={() => openItem(no)}>
      {no}
    </button>
  );
}

export function Where({ i }: { i: ItemDto }) {
  const { d } = useApp();
  if (i.status === 'gone' && i.gone)
    return (
      <>
        <Tag cls="bad">{i.gone.reason}</Tag> <span className="meta">since {ymd(i.gone.at)} ({i.gone.by})</span>
      </>
    );
  if (i.status === 'in')
    return (
      <>
        <span className="code">{d.addr(i.loc)}</span> <span className="meta">{d.addrLong(i.loc)}</span>
        {i.repair && (
          <>
            {' '}
            <Tag cls="warn">{i.repair}</Tag>
          </>
        )}
      </>
    );
  if (i.out)
    return (
      <>
        <Tag cls="warn">Taken out</Tag>{' '}
        <span className="meta">
          by {i.out.by} on {ymd(i.out.at)}, {i.out.why.toLowerCase()}
          {i.out.project ? ` (${d.projTxt(i.out.project)})` : ''}
        </span>
      </>
    );
  return <span className="meta">Not on a shelf</span>;
}

export function Bar({ q, min }: { q: number; min: number }) {
  if (!min) return null;
  return (
    <div className="bar">
      <i className={q < min ? 'low' : ''} style={{ width: `${Math.min(100, (q / (min * 2)) * 100)}%` }} />
    </div>
  );
}

export function ActTable({ list, compact }: { list: ActivityDto[]; compact?: boolean }) {
  if (!list.length) return <p className="meta">No activities match.</p>;
  const cls = (a: string) => (a === 'Take out' ? 'warn' : a === 'Register' ? 'ok' : ['QC check', 'Note'].includes(a) ? 'soft' : '');
  return (
    <div className="panel scroll" style={{ padding: '4px 8px' }}>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Time</th>
            <th>User</th>
            <th>Action</th>
            <th>Item number</th>
            {!compact && (
              <>
                <th>From</th>
                <th>To</th>
              </>
            )}
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {list.map((a) => (
            <tr key={a.id}>
              <td style={{ whiteSpace: 'nowrap' }}>{ymd(a.at)}</td>
              <td>{hm(a.at)}</td>
              <td>{a.user}</td>
              <td>
                <Tag cls={cls(a.action)}>{a.action}</Tag>
              </td>
              <td>
                <ItemLink no={a.item} />
              </td>
              {!compact && (
                <>
                  <td className="code">{a.from}</td>
                  <td className="code">{a.to}</td>
                </>
              )}
              <td>{a.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The shelf drawing: levels from the top down, left / middle / right. */
export function ShelfDraw({
  sh,
  pick,
  onPick,
  onOpen,
}: {
  sh: ShelfDto;
  pick?: { level?: number; pos?: Pos };
  onPick?: (level: number, pos: Pos) => void;
  onOpen?: (level: number, pos: Pos) => void;
}) {
  const { d } = useApp();
  const cls = d.isLocked(sh) ? 'locked' : d.isRepair(sh) ? 'repair' : '';
  const levels = Array.from({ length: sh.levels }, (_, k) => sh.levels - k);
  return (
    <>
      <div className={`shelf ${cls}`}>
        {levels.map((l) => (
          <div className="lvl" key={l}>
            <div className="lvn" title={`Level ${l}`}>
              {l}
            </div>
            {(['L', 'M', 'R'] as Pos[]).map((p) => {
              const its = d.itemsAt(sh.code, l, p);
              const sel = onPick && pick?.level === l && pick?.pos === p;
              const bad = its.some((i) => !d.qcOf(i).ok) && !d.isRepair(sh);
              const inner = (
                <>
                  <span className="pn">{POS[p]}</span>
                  {its.slice(0, 2).map((i) => (
                    <span className="it" key={i.no}>
                      {d.artOf(i).name}
                    </span>
                  ))}
                  {its.length > 2 && <span className="pn">+{its.length - 2} more</span>}
                </>
              );
              return onPick ? (
                <button key={p} className={`cell pick ${sel ? 'sel' : ''} ${bad ? 'qcbad' : ''}`} onClick={() => onPick(l, p)} aria-label={`Level ${l} ${POS[p]}`}>
                  {inner}
                </button>
              ) : (
                <button key={p} className={`cell ${bad ? 'qcbad' : ''}`} disabled={!its.length} onClick={() => onOpen?.(l, p)} aria-label={`Level ${l} ${POS[p]}`}>
                  {inner}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="floor">floor · level 1 is the bottom</div>
    </>
  );
}

export function Spinner() {
  return (
    <div className="center">
      <div className="spin" />
    </div>
  );
}

export function Err({ e }: { e: string }) {
  return e ? <div className="err">{e}</div> : null;
}
